import { useEffect, useState, useCallback } from "react";
import {
  Card, Typography, Button, Tag, Switch, Modal, Form, Input, InputNumber, Radio, Dropdown, Alert, Tooltip, message,
} from "antd";
import { PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { api } from "../../services/api";
import { BACKEND } from "../../templates/4/utils";

const { Text } = Typography;

// Cómo se lee el ajuste: ip.web guarda un solo número, positivo para
// descuento y negativo para recargo.
const ajusteTexto = (d) => (d > 0 ? `${d}% de descuento` : d < 0 ? `${Math.abs(d)}% de recargo` : "Sin ajuste");

// Las claves nunca llegan al navegador: solo si están cargadas. Vacío no las
// toca; "Borrar" manda "" a propósito.
const CampoSecreto = ({ label, cargada, valor, onChange, borrar, onBorrar, ayuda, id }) => (
  <div>
    <label htmlFor={id} style={{ fontSize: 13, color: "#666" }}>{label}</label>
    <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 4 }}>
      <Input.Password
        id={id}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        disabled={borrar}
        placeholder={borrar ? "Se va a borrar al guardar" : cargada ? "Cargada. Escribí una nueva para reemplazarla" : "Sin cargar"}
        autoComplete="new-password"
      />
      {cargada && (
        <Button size="small" type={borrar ? "default" : "text"} danger={!borrar} onClick={onBorrar}>
          {borrar ? "No borrar" : "Borrar"}
        </Button>
      )}
    </div>
    {ayuda && <Text type="secondary" style={{ fontSize: 12 }}>{ayuda}</Text>}
  </div>
);

export const PasarelasTienda = ({ slug }) => {
  const [pasarelas, setPasarelas] = useState([]);
  const [disponibles, setDisponibles] = useState([]);
  const [editando, setEditando] = useState(null);

  const cargar = useCallback(async () => {
    const res = await api.platform.tiendas.pasarelas(slug);
    if (res.status === "success") {
      setPasarelas(res.data.pasarelas || []);
      setDisponibles(res.data.disponibles || []);
    }
  }, [slug]);

  useEffect(() => { cargar(); }, [cargar]);

  const agregar = async (codigo) => {
    const res = await api.platform.tiendas.crearPasarela(slug, codigo);
    if (res.status === "success") {
      message.success("Agregado. Configuralo y activalo cuando esté listo.");
      await cargar();
      setEditando(res.data);
    } else {
      message.error(res.message || "No se pudo agregar");
    }
  };

  const cambiarActivo = async (p, activo) => {
    const res = await api.platform.tiendas.actualizarPasarela(slug, p.id, { activo });
    if (res.status === "success") {
      setPasarelas((ps) => ps.map((x) => (x.id === p.id ? res.data : x)));
    } else {
      message.error(res.message || "No se pudo cambiar");
    }
  };

  return (
    <Card style={{ borderRadius: 12, marginTop: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 4 }}>
        <Text strong>Medios de pago</Text>
        {disponibles.length > 0 && (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: disponibles.map((d) => ({ key: d.codigo, label: d.nombre })),
              onClick: ({ key }) => agregar(key),
            }}
          >
            <Button size="small" icon={<PlusOutlined />}>Agregar</Button>
          </Dropdown>
        )}
      </div>
      <Text type="secondary" style={{ display: "block", marginBottom: 16, fontSize: 13 }}>
        Lo que ve el cliente al elegir cómo pagar. Las claves no se muestran nunca: solo si están cargadas.
      </Text>

      {pasarelas.length === 0 ? (
        <Text type="secondary">Esta tienda todavía no tiene medios de pago.</Text>
      ) : pasarelas.map((p, i) => (
        <div key={p.id} style={{
          display: "flex", alignItems: "center", gap: 12, padding: "10px 0",
          borderTop: i === 0 ? "none" : "1px solid #f0f0f0", flexWrap: "wrap",
        }}>
          <div style={{ flex: "1 1 220px", minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <Text strong>{p.nombre}</Text>
              {!p.publico && <Tag>Oculto en el checkout</Tag>}
              {p.codigo === "mercadopago" && (p.tiene_access_token
                ? <Tag color="green">Access token cargado</Tag>
                : <Tag color="orange">Falta el access token</Tag>)}
              {p.codigo === "paypal" && (p.tiene_access_token && p.tiene_webhook_secret
                ? <Tag color="green">Credenciales cargadas</Tag>
                : <Tag color="orange">Faltan las credenciales</Tag>)}
              {p.codigo === "paypal" && p.sandbox && <Tag>Sandbox</Tag>}
              {p.codigo === "paypal" && !p.webhook_id && <Tag color="orange">Sin webhook</Tag>}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {ajusteTexto(p.descuento)}{p.comision ? ` · comisión ${p.comision}%` : ""}
            </Text>
          </div>
          <Tooltip title={p.activo ? "Activo: el cliente lo puede elegir" : "Inactivo: no aparece"}>
            <Switch checked={p.activo} onChange={(v) => cambiarActivo(p, v)} />
          </Tooltip>
          <Button size="small" icon={<SettingOutlined />} onClick={() => setEditando(p)}>Configurar</Button>
        </div>
      ))}

      <EditarPasarela
        slug={slug}
        pasarela={editando}
        onClose={() => setEditando(null)}
        onGuardada={(p) => { setPasarelas((ps) => ps.map((x) => (x.id === p.id ? p : x))); setEditando(null); }}
      />
    </Card>
  );
};

const EditarPasarela = ({ slug, pasarela, onClose, onGuardada }) => {
  const [form] = Form.useForm();
  const [tipoAjuste, setTipoAjuste] = useState("ninguno");
  const [secretos, setSecretos] = useState({});
  const [borrar, setBorrar] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pasarela) return;
    setTipoAjuste(pasarela.descuento > 0 ? "descuento" : pasarela.descuento < 0 ? "recargo" : "ninguno");
    setSecretos({});
    setBorrar({});
    setError("");
    // El form se reusa entre pasarelas: sin esto quedarían los datos de la
    // anterior, porque initialValues no pisa lo que ya está cargado.
    form.resetFields();
  }, [pasarela, form]);

  if (!pasarela) return null;
  const esMP = pasarela.codigo === "mercadopago";
  const esPP = pasarela.codigo === "paypal";

  const guardar = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const porcentaje = tipoAjuste === "ninguno" ? 0 : Number(values.porcentaje || 0);
    const data = {
      nombre: values.nombre,
      descripcion: values.descripcion || "",
      descuento: tipoAjuste === "recargo" ? -porcentaje : porcentaje,
      comision: Number(values.comision || 0),
      publico: values.publico,
    };
    if (esMP) {
      data.public_key = values.public_key || "";
      // Solo viaja la clave que se escribió o se pidió borrar.
      for (const campo of ["access_token", "webhook_secret", "notif_secret"]) {
        if (borrar[campo]) data[campo] = "";
        else if (secretos[campo]) data[campo] = secretos[campo];
      }
    }
    if (esPP) {
      // El client id y el secret viajan en los mismos campos que las claves de MP.
      for (const campo of ["access_token", "webhook_secret"]) {
        if (borrar[campo]) data[campo] = "";
        else if (secretos[campo]) data[campo] = secretos[campo];
      }
      data.webhook_id = values.webhook_id || "";
      data.sandbox = !!values.sandbox;
    }
    setGuardando(true);
    setError("");
    try {
      const res = await api.platform.tiendas.actualizarPasarela(slug, pasarela.id, data);
      if (res.status === "success") {
        message.success("Guardado");
        onGuardada(res.data);
      } else {
        setError(res.message || "No se pudo guardar");
      }
    } finally {
      setGuardando(false);
    }
  };

  const secreto = (campo, label, cargada, ayuda) => (
    <CampoSecreto
      id={`secreto-${campo}`}
      label={label}
      cargada={cargada}
      ayuda={ayuda}
      valor={secretos[campo] || ""}
      onChange={(v) => setSecretos((s) => ({ ...s, [campo]: v }))}
      borrar={!!borrar[campo]}
      onBorrar={() => setBorrar((b) => ({ ...b, [campo]: !b[campo] }))}
    />
  );

  return (
    <Modal
      open
      title={pasarela.nombre}
      okText="Guardar"
      onOk={guardar}
      onCancel={guardando ? undefined : onClose}
      confirmLoading={guardando}
      okButtonProps={{ style: { background: "#4f46e5" } }}
      width={560}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} />}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          nombre: pasarela.nombre,
          descripcion: pasarela.descripcion || "",
          porcentaje: Math.abs(pasarela.descuento || 0),
          comision: pasarela.comision || 0,
          publico: pasarela.publico,
          public_key: pasarela.public_key || "",
          webhook_id: pasarela.webhook_id || "",
          sandbox: !!pasarela.sandbox,
        }}
      >
        <Form.Item label="Nombre que ve el cliente" name="nombre" rules={[{ required: true, whitespace: true, message: "Falta el nombre" }]}>
          <Input />
        </Form.Item>
        <Form.Item label="Descripción" name="descripcion">
          <Input placeholder="Opcional" />
        </Form.Item>

        <Form.Item label="Ajuste al precio" style={{ marginBottom: 8 }}>
          <Radio.Group value={tipoAjuste} onChange={(e) => setTipoAjuste(e.target.value)}>
            <Radio value="ninguno">Sin ajuste</Radio>
            <Radio value="descuento">Descuento</Radio>
            <Radio value="recargo">Recargo</Radio>
          </Radio.Group>
        </Form.Item>
        {tipoAjuste !== "ninguno" && (
          <Form.Item name="porcentaje" rules={[{ required: true, message: "Falta el porcentaje" }]}
            extra={tipoAjuste === "descuento" ? "Se descuenta del total al elegir este medio." : "Se suma al total al elegir este medio."}>
            <InputNumber min={0} max={100} addonAfter="%" style={{ width: 160 }} />
          </Form.Item>
        )}

        <Form.Item label="Comisión de la pasarela" name="comision"
          extra="No cambia el precio: es lo que cobra la pasarela, para saber cuánto le queda a la tienda.">
          <InputNumber min={0} max={100} step={0.1} addonAfter="%" style={{ width: 160 }} />
        </Form.Item>

        <Form.Item name="publico" valuePropName="checked" label="Se ofrece en el checkout"
          extra="Apagado, sigue activo pero el cliente no lo ve (por ejemplo, solo para cobros cargados desde el admin).">
          <Switch />
        </Form.Item>

        {esMP && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
            <Text strong style={{ fontSize: 13 }}>Credenciales de Mercado Pago</Text>
            {secreto("access_token", "Access token", pasarela.tiene_access_token, "Sin esto no se puede activar.")}
            <Form.Item label="Public key" name="public_key" style={{ marginBottom: 0 }}
              extra="No es secreta: la usa el navegador para mostrar el formulario de pago.">
              <Input />
            </Form.Item>
            {secreto("webhook_secret", "Secret del webhook", pasarela.tiene_webhook_secret, "El que da Mercado Pago para el webhook configurado en su panel.")}
            {secreto("notif_secret", "Secret de notificación", pasarela.tiene_notif_secret, "El de las notificaciones de cada pago (notification_url).")}
          </div>
        )}

        {esPP && (
          <div style={{ display: "flex", flexDirection: "column", gap: 14, borderTop: "1px solid #f0f0f0", paddingTop: 16 }}>
            <Text strong style={{ fontSize: 13 }}>Credenciales de PayPal</Text>
            <Text type="secondary" style={{ fontSize: 12, marginTop: -8 }}>
              Se cobra en dólares, al blue del día. Salen de developer.paypal.com → Apps &amp; Credentials.
            </Text>
            {secreto("access_token", "Client ID", pasarela.tiene_access_token, "Sin esto y el secret no se puede activar.")}
            {secreto("webhook_secret", "Secret", pasarela.tiene_webhook_secret)}
            <Form.Item name="sandbox" valuePropName="checked" label="Modo sandbox" style={{ marginBottom: 0 }}
              extra="Para probar con cuentas de prueba de PayPal. Apagado, cobra de verdad. Las credenciales de sandbox y las de producción son distintas.">
              <Switch />
            </Form.Item>
            <div>
              <Text style={{ fontSize: 13, color: "#666" }}>URL del webhook</Text>
              <Input readOnly value={`${BACKEND}/webhook/paypal/${slug}`} style={{ marginTop: 4 }}
                onFocus={(e) => e.target.select()} />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cargala en la app de PayPal (Webhooks), con los eventos Checkout order approved y Payment capture (completed, denied y refunded).
              </Text>
            </div>
            <Form.Item label="Webhook ID" name="webhook_id" style={{ marginBottom: 0 }}
              extra="El que muestra PayPal al crear el webhook. Sin él no se puede verificar la firma y los avisos de PayPal se ignoran: el pago igual se confirma cuando el cliente vuelve a la tienda.">
              <Input placeholder="Ej: 8PT597110X687430LKGECATA" />
            </Form.Item>
          </div>
        )}
      </Form>
    </Modal>
  );
};
